import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { formatCurrency } from '../../../../../../../utils/CurrencyUtils';
import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import { TruckBudgetedVsSpentData } from '../../../../../../../types/apiTypes';
import { ClipLoader } from 'react-spinners';
import { buildPageWindow } from '../../../../../../../utils/PaginationUtils';
import { DemoDataWrapper } from '../../../home/components/DemoDataBadge';

const ITEMS_PER_PAGE = 2;
const SEGMENT_SEPARATOR = '#ffffff';
const SEGMENT_SEPARATOR_WIDTH = 2;

const TruckBudgetedSpentBar: React.FC<{
  truck: TruckBudgetedVsSpentData;
  isAllTrucks?: boolean;
}> = ({ truck, isAllTrucks = false }) => {
  const [hovered, setHovered] = useState<{ key: string; color: string } | null>(
    null
  );

  // Prepare data for budgeted bar
  const budgetedData = [
    {
      name: 'Budgeted',
      ...truck.budgetedCategories.reduce(
        (acc, cat) => {
          acc[cat.category] = cat.amount;
          return acc;
        },
        {} as Record<string, number>
      ),
    },
  ];

  // Prepare data for spent bar
  const spentData = [
    {
      name: 'Spent',
      ...truck.spentCategories.reduce(
        (acc, cat) => {
          acc[cat.category] = cat.amount;
          return acc;
        },
        {} as Record<string, number>
      ),
    },
  ];

  // Custom tooltip that shows based on hoveredSegment state
  const CustomTooltip = ({ active, payload }: any) => {
    if (!(active && payload && payload.length && hovered?.key)) return null;
    const activeEntry = payload.find((e: any) => e.dataKey === hovered.key);
    if (!activeEntry || !activeEntry.value) return null;

    return (
      <div className="rounded-lg bg-gray-800 p-3 text-white shadow-lg">
        <div className="flex items-center">
          <span
            className="mr-2 inline-block h-3 w-3 rounded"
            style={{ backgroundColor: hovered.color }}
          />
          <p className="text-[14px] font-semibold">
            {hovered.key}: {formatCurrency(activeEntry.value)}
          </p>
        </div>
      </div>
    );
  };

  // Calculate which bar should be full width
  const maxAmount = Math.max(truck.totalBudgeted, truck.totalSpent);

  if (truck.totalBudgeted === 0 && truck.totalSpent === 0) {
    return (
      <div className={``}>
        <h3 className={`text-lg font-semibold text-gray-900`}>
          {truck.truckName}
        </h3>
        <div className="mt-2 flex h-[80px] w-full items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 text-center sm:px-2">
          <p className="text-sm text-gray-500">
            Budgeting data will appear here once transactions are recorded for{' '}
            {dayjs().format('MMMM')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={``}>
      <h3 className={`text-lg font-semibold text-gray-900`}>
        {truck.truckName}
      </h3>

      {/* Budgeted Bar */}
      <div className="mb-2">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Budgeted</span>
          <span className="text-sm font-semibold text-green-700">
            Total: {formatCurrency(truck.totalBudgeted)}
          </span>
        </div>
        <ResponsiveContainer width="100%" height={20}>
          <BarChart
            layout="vertical"
            data={budgetedData}
            margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
            className="rounded-lg"
          >
            <XAxis type="number" hide domain={[0, maxAmount]} />
            <YAxis type="category" dataKey="name" hide />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: 'transparent' }}
              position={{ x: undefined, y: undefined }}
            />
            {truck.budgetedCategories.map((cat, index) => (
              <Bar
                key={cat.category}
                dataKey={cat.category}
                stackId="a"
                stroke={SEGMENT_SEPARATOR}
                strokeWidth={SEGMENT_SEPARATOR_WIDTH}
                fill={cat.color}
                name={cat.category}
                radius={
                  index === 0
                    ? [4, 0, 0, 4] // First bar - rounded left
                    : index === truck.budgetedCategories.length - 1
                      ? [0, 4, 4, 0] // Last bar - rounded right
                      : undefined // Middle bars - no radius
                }
                onMouseEnter={() =>
                  setHovered({ key: cat.category, color: cat.color })
                }
                onMouseLeave={() => setHovered(null)}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Spent Bar */}
      <div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Spent</span>
          <span className="text-sm font-semibold text-red-700">
            Total: {formatCurrency(truck.totalSpent)}
          </span>
        </div>
        <ResponsiveContainer width="100%" height={20}>
          <BarChart
            layout="vertical"
            data={spentData}
            margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
          >
            <XAxis type="number" hide domain={[0, maxAmount]} />
            <YAxis type="category" dataKey="name" hide />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: 'transparent' }}
              position={{ x: undefined, y: undefined }}
            />
            {truck.spentCategories.map((cat, index) => (
              <Bar
                key={cat.category}
                dataKey={cat.category}
                stackId="a"
                fill={cat.color}
                name={cat.category}
                stroke={SEGMENT_SEPARATOR}
                strokeWidth={SEGMENT_SEPARATOR_WIDTH}
                radius={
                  index === 0
                    ? [4, 0, 0, 4] // First bar - rounded left
                    : index === truck.spentCategories.length - 1
                      ? [0, 4, 4, 0] // Last bar - rounded right
                      : undefined // Middle bars - no radius
                }
                onMouseEnter={() =>
                  setHovered({ key: cat.category, color: cat.color })
                }
                onMouseLeave={() => setHovered(null)}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

interface TrucksBudgetedSpentStackedBarsProps {
  addClass?: string;
  trucksData: TruckBudgetedVsSpentData[];
  loading: boolean;
  isPreActive: boolean;
}

const TrucksBudgetedSpentStackedBars: React.FC<
  TrucksBudgetedSpentStackedBarsProps
> = ({ addClass, trucksData = [], loading, isPreActive }) => {
  const [currentPage, setCurrentPage] = useState(1);

  // Separate "All Trucks" from individual trucks
  const allTrucksData = trucksData.find((t) => t.truckId === 'all');
  const individualTrucks = trucksData.filter((t) => t.truckId !== 'all');

  // Calculate pagination
  const totalPagesRaw = Math.ceil(individualTrucks.length / ITEMS_PER_PAGE);
  const totalPages = Math.max(1, totalPagesRaw);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentTrucks = individualTrucks.slice(startIndex, endIndex);

  const hasPagination = totalPages > 1;

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1); // or Math.min(currentPage, totalPages)
    }
  }, [currentPage, totalPages]);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <DemoDataWrapper showBadge={isPreActive}>
      <div
        className={`flex h-[600px] max-h-[600px] w-full flex-col overflow-hidden border-[1px] border-bordergray bg-white ${addClass}`}
      >
        <div className="flex h-[71px] min-h-[71px] items-center justify-start border-b-[1px] border-bordergray p-4">
          <h2 className="text-[16px] font-semibold leading-[24px] text-primary">
            MTD Budgeted Savings & Expenses
          </h2>
        </div>

        {loading ? (
          <div className="flex h-[600px] items-center justify-center p-8">
            <ClipLoader
              color="#1D213B"
              loading={loading}
              size={60}
              aria-label="Loading Spinner"
              data-testid="loader"
            />
          </div>
        ) : trucksData.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-8 px-4 text-center sm:px-8">
            <p className="mb-2 text-[16px] font-semibold leading-[24px] text-primary">
              No Data Available
            </p>
            <p className="text-[14px] leading-[20px] text-graysecondary">
              Budgeting data will appear here once transactions are recorded for{' '}
              {dayjs().format('MMMM')}
            </p>
          </div>
        ) : (
          <div
            className={`flex h-full flex-col ${hasPagination ? 'justify-between' : 'justify-start'} px-4 pt-4`}
          >
            {/* All Trucks Summary */}
            <div>
              {allTrucksData && (
                <div className="mb-2 rounded-lg border-[1px] border-bordergray bg-gray-50 p-2">
                  <TruckBudgetedSpentBar
                    truck={allTrucksData}
                    isAllTrucks={true}
                  />
                </div>
              )}
              <div className="space-y-2">
                {currentTrucks.map((truck, idx) => (
                  <div
                    key={truck.truckId}
                    className={`${idx !== currentTrucks.length - 1 && `border-b-[1px] border-bordergray`} p-2`}
                  >
                    <TruckBudgetedSpentBar truck={truck} />
                  </div>
                ))}
              </div>
            </div>

            {/* Pagination Controls */}
            {hasPagination && (
              <div className="mt-6 flex items-center justify-between pb-4">
                <button
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                  className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    currentPage === 1
                      ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                      : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                  aria-label="Previous page"
                >
                  <CaretLeftIcon className="h-4 w-4" />
                  Previous
                </button>

                <div className="mx-2 flex items-center justify-center gap-2 whitespace-nowrap">
                  {buildPageWindow(
                    totalPages,
                    currentPage,
                    7, // <= force compact; last appears only near the end
                    { revealLastOffset: 4 } // last shows starting at page 6 of 10
                  ).map((item, idx) =>
                    item === '…' ? (
                      <span
                        key={`ellipsis-${idx}`}
                        className="shrink-0 px-1 text-gray-400"
                      >
                        …
                      </span>
                    ) : (
                      <button
                        key={item}
                        onClick={() => setCurrentPage(item)}
                        className={`h-8 w-8 shrink-0 rounded-lg text-sm font-medium transition-colors ${
                          currentPage === item
                            ? 'bg-blue-600 text-white'
                            : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
                        }`}
                        aria-current={currentPage === item ? 'page' : undefined}
                      >
                        {item}
                      </button>
                    )
                  )}
                </div>

                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    currentPage === totalPages
                      ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                      : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                  aria-label="Next page"
                >
                  Next
                  <CaretRightIcon className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </DemoDataWrapper>
  );
};

export default TrucksBudgetedSpentStackedBars;
